import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  type TableColumnsType,
} from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useState } from 'react';

import {
  useClientsDeleteArticle,
  useClientsListArticles,
  useClientsUpsertArticle,
} from '@/api/generated/endpoints/clients/clients';
import type { ArticleOut, ProductRef } from '@/api/generated/model';
import { useHasRole } from '@/auth/useHasRole';
import { ProductSelect } from '@/components/ProductSelect';
import { QtyCell, QtyInput } from '@/components/qty';
import { problemMessage } from '@/i18n/problem';
import { applyProblem, problemText } from '@/lib/problemForm';
import { qtyRule, type QtyValue } from '@/lib/qty';
import { invalidatePath } from '@/lib/queryCache';
import { uomLabel } from '@/lib/units';
import { useIsMobile } from '@/lib/useIsMobile';

interface ArticleForm {
  article: string;
  product_id: number | null;
  kg_per_pcs: QtyValue;
}

/** Артикулы покупателя (графа «А» УПД) → наш товар; для фасовки — вес одной штуки. */
export function ArticlesTab({ clientId }: { clientId: number }) {
  const { t } = useTranslation();
  const mobile = useIsMobile();
  const canEdit = useHasRole('storekeeper');
  const canDelete = useHasRole('manager');
  const list = useClientsListArticles(clientId);
  const [editing, setEditing] = useState<ArticleOut | 'new' | null>(null);
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const remove = useClientsDeleteArticle({
    mutation: {
      onSuccess: () => invalidatePath(queryClient, `/api/v1/clients/${clientId}`),
      onError: (e) => void message.error(problemText(e)),
    },
  });

  const columns: TableColumnsType<ArticleOut> = [
    { key: 'article', title: t('articles.colArticle'), dataIndex: 'article', width: 160 },
    {
      key: 'product',
      title: t('articles.colProduct'),
      render: (_: unknown, a) => (
        <Space>
          {a.product.name}
          <Tag>{uomLabel(t, a.product.base_uom)}</Tag>
        </Space>
      ),
    },
    {
      key: 'kg_per_pcs',
      title: t('articles.colKgPerPcs'),
      dataIndex: 'kg_per_pcs',
      width: 120,
      render: (kg: string | null) => (kg ? <QtyCell value={kg} uom="kg" /> : '—'),
    },
    {
      key: 'updated_at',
      title: t('articles.colUpdated'),
      dataIndex: 'updated_at',
      width: 140,
      render: (d: string) => dayjs(d).format('DD.MM.YYYY'),
    },
    {
      key: 'actions',
      width: 90,
      render: (_: unknown, a) => (
        <Space size={0}>
          {canEdit && (
            <Button
              type="text"
              icon={<EditOutlined />}
              aria-label={t('common.edit')}
              onClick={() => {
                setEditing(a);
              }}
            />
          )}
          {canDelete && (
            <Popconfirm
              title={t('articles.deleteConfirm')}
              onConfirm={() => {
                remove.mutate({ clientId, article: a.article });
              }}
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                aria-label={t('common.delete')}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // Телефон (T-177): артикул, товар и вес штуки — одной ячейкой; кнопки — отдельной узкой колонкой.
  const mobileColumns: TableColumnsType<ArticleOut> = [
    {
      key: 'article',
      title: t('articles.colArticle'),
      render: (_: unknown, a) => (
        <Space direction="vertical" size={2}>
          <Typography.Text strong>{a.article}</Typography.Text>
          <span>
            {a.product.name} <Tag>{uomLabel(t, a.product.base_uom)}</Tag>
          </span>
          {a.kg_per_pcs && (
            <Typography.Text type="secondary">
              {t('articles.colKgPerPcs')}: <QtyCell value={a.kg_per_pcs} uom="kg" />
            </Typography.Text>
          )}
        </Space>
      ),
    },
    ...columns.filter((c) => c.key === 'actions'),
  ];

  return (
    <>
      <Typography.Paragraph type="secondary">{t('articles.hint')}</Typography.Paragraph>
      {canEdit && (
        <Button
          icon={<PlusOutlined />}
          style={{ marginBottom: 12 }}
          onClick={() => {
            setEditing('new');
          }}
        >
          {t('articles.add')}
        </Button>
      )}
      <Table<ArticleOut>
        rowKey="article"
        size="small"
        columns={mobile ? mobileColumns : columns}
        dataSource={list.data}
        loading={list.isFetching}
        pagination={false}
        locale={{ emptyText: t('articles.empty') }}
      />
      <ArticleModal
        clientId={clientId}
        article={editing}
        onClose={() => {
          setEditing(null);
        }}
      />
    </>
  );
}

function ArticleModal({
  clientId,
  article,
  onClose,
}: {
  clientId: number;
  article: ArticleOut | 'new' | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm<ArticleForm>();
  const [product, setProduct] = useState<ProductRef | null>(null);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const existing = article !== 'new' ? article : null;
  const shown = product ?? existing?.product ?? null;
  const upsert = useClientsUpsertArticle({
    mutation: {
      onSuccess: async () => {
        await invalidatePath(queryClient, `/api/v1/clients/${clientId}`);
        setProduct(null);
        onClose();
      },
      onError: (e) => {
        if (!applyProblem(form, e)) setError(problemMessage(e));
      },
    },
  });

  return (
    <Modal
      title={existing ? t('articles.title', { article: existing.article }) : t('articles.new')}
      open={article !== null}
      onCancel={() => {
        setError(null);
        setProduct(null);
        onClose();
      }}
      okText={t('common.save')}
      okButtonProps={{ loading: upsert.isPending }}
      onOk={() => {
        form.submit();
      }}
      destroyOnHidden
    >
      <Form<ArticleForm>
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={
          existing
            ? {
                article: existing.article,
                product_id: existing.product.id,
                kg_per_pcs: existing.kg_per_pcs,
              }
            : { kg_per_pcs: null }
        }
        onFinish={(v) => {
          setError(null);
          const kg =
            shown?.base_uom === 'kg' && typeof v.kg_per_pcs === 'string' ? v.kg_per_pcs : null;
          upsert.mutate({
            clientId,
            article: v.article.trim(),
            data: { product_id: v.product_id ?? 0, kg_per_pcs: kg },
          });
        }}
      >
        <Form.Item
          name="article"
          label={t('articles.article')}
          rules={[{ required: true, whitespace: true, message: t('articles.articleRequired') }]}
        >
          <Input disabled={existing !== null} maxLength={64} autoFocus />
        </Form.Item>
        <Form.Item
          name="product_id"
          label={t('articles.product')}
          rules={[{ required: true, message: t('articles.productRequired') }]}
        >
          <ProductSelect
            initial={existing?.product}
            onChange={(_, p) => {
              setProduct(p);
            }}
          />
        </Form.Item>
        {shown?.base_uom === 'kg' && (
          <Form.Item
            name="kg_per_pcs"
            label={t('articles.kgPerPcs')}
            rules={[qtyRule({ positive: true })]}
            extra={t('articles.kgPerPcsHint')}
          >
            <QtyInput uom="kg" places={4} unitLabel={t('uom.kgPerPcs')} style={{ width: 200 }} />
          </Form.Item>
        )}
      </Form>
      {error && <Alert type="error" showIcon message={error} />}
    </Modal>
  );
}
